import { clearNetworkReconnectTimeout } from './networkDrop';
import type { JanusCallRefs, JanusCallSetters } from './sessionRefs';
import type { SendJanusFn } from './types';

export function detachAllRemoteSubscribers(r: JanusCallRefs, sendJanus: SendJanusFn): void {
  cleanupSubscribers(r, sendJanus);
}

export function cleanupSubscribers(r: JanusCallRefs, sendJanus: SendJanusFn): void {
  r.subscriberFeedsRef.current.forEach((sub) => {
    if (r.sessionIdRef.current) {
      sendJanus({
        janus: 'hangup',
        session_id: r.sessionIdRef.current,
        handle_id: sub.handleId,
      });
    }
    sub.pc?.close();
  });
  r.subscriberFeedsRef.current = [];
  r.subscribedFeedIdsRef.current.clear();
  r.pendingSubscriberAttachesRef.current.clear();
}

export function cancelWebSocketReconnect(r: JanusCallRefs): void {
  if (r.wsReconnectTimerRef.current) {
    clearTimeout(r.wsReconnectTimerRef.current);
    r.wsReconnectTimerRef.current = null;
  }
  r.wsReconnectAttemptsRef.current = 0;
}

export function disposeWebSocketTransport(r: JanusCallRefs): void {
  cancelWebSocketReconnect(r);
  if (r.keepAliveIntervalRef.current) {
    clearInterval(r.keepAliveIntervalRef.current);
    r.keepAliveIntervalRef.current = null;
  }
  if (r.wsRef.current) {
    r.wsRef.current.onopen = null;
    r.wsRef.current.onmessage = null;
    r.wsRef.current.onerror = null;
    r.wsRef.current.onclose = null;
    if (r.wsRef.current.readyState === WebSocket.OPEN) {
      r.wsRef.current.close();
    }
    r.wsRef.current = null;
  }
  r.sessionIdRef.current = null;
  r.handleIdRef.current = null;
  r.isSessionReadyRef.current = false;
  r.pendingSubscriberAttachesRef.current.clear();
}

export function cleanupCall(
  r: JanusCallRefs,
  set: JanusCallSetters,
  sendJanus: SendJanusFn,
  cleanupSubs: () => void
): void {
  cancelWebSocketReconnect(r);

  if (r.publisherPcRef.current) {
    r.publisherPcRef.current.close();
    r.publisherPcRef.current = null;
  }
  cleanupSubs();

  r.isJoinedRef.current = false;
  r.isPublisherJoinedRef.current = false;
  r.isPublisherConfiguredRef.current = false;
  r.myPublisherIdRef.current = null;
  r.myPrivateIdRef.current = null;
  r.micDisconnectHandledRef.current = false;
  r.speakerPlaybackWarnedRef.current = false;
  r.recoverLocalAudioRunningRef.current = false;
  r.pendingLocalStreamRefreshRef.current = false;
  r.networkDropPendingRef.current = false;
  r.lastRepublishOutboundAtRef.current = 0;
  r.lastRecoverLocalAudioAtRef.current = 0;
  clearNetworkReconnectTimeout(r);

  if (r.handleIdRef.current && r.sessionIdRef.current) {
    sendJanus({
      janus: 'detach',
      session_id: r.sessionIdRef.current,
      handle_id: r.handleIdRef.current,
      transaction: 'detach',
    });
  }
  r.handleIdRef.current = null;

  set.setLocalStream(null);
  set.setRemoteStream(null);
  set.setCallStatus('idle');
  r.mediaConnectionLostSignaledRef.current = false;
}

export function cleanupWebSocket(
  r: JanusCallRefs,
  set: JanusCallSetters
): void {
  r.intentionalWsCloseRef.current = true;
  disposeWebSocketTransport(r);
  set.setCallStatus('idle');
}
